import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { submitLead, formatApiError } from "@/lib/api";

export const FORM_CONFIGS = {
  recherche: {
    submitLabel: "Lancer ma recherche",
    submitTestId: "form-private-search-submit",
    fields: [
      { name: "name", label: "Nom complet", required: true },
      { name: "phone", label: "Téléphone", type: "tel", required: true },
      { name: "email", label: "Courriel", type: "email", required: true },
      {
        name: "Type de véhicule", label: "Type de véhicule", type: "select",
        options: ["VUS", "Berline", "Camion", "Véhicule de luxe", "Véhicule sportif", "Véhicule familial", "Véhicule économique", "Véhicule commercial", "Autre"],
      },
      { name: "Budget", label: "Budget", placeholder: "Ex. : 45 000 $" },
      { name: "Année souhaitée", label: "Année souhaitée", placeholder: "Ex. : 2022 ou plus récent" },
      { name: "Kilométrage maximal", label: "Kilométrage maximal", placeholder: "Ex. : 60 000 km" },
      { name: "Commentaires", label: "Commentaires", type: "textarea", full: true, placeholder: "Marque, modèle, couleur, équipement souhaité…" },
    ],
  },
  financement: {
    submitLabel: "Soumettre ma demande",
    submitTestId: "form-financing-submit",
    fields: [
      { name: "name", label: "Nom complet", required: true },
      { name: "phone", label: "Téléphone", type: "tel", required: true },
      { name: "email", label: "Courriel", type: "email", required: true },
      { name: "Véhicule recherché", label: "Véhicule recherché", placeholder: "Ex. : VUS compact, berline…" },
      { name: "Budget approximatif", label: "Budget approximatif", placeholder: "Ex. : 30 000 $" },
      { name: "Mise de fonds", label: "Mise de fonds", placeholder: "Ex. : 2 000 $" },
      {
        name: "Situation de crédit", label: "Situation de crédit", type: "select",
        options: ["Excellente", "Bonne", "1re chance au crédit", "2e chance au crédit", "3e chance au crédit", "Je préfère en discuter"],
      },
      { name: "Commentaires", label: "Commentaires", type: "textarea", full: true },
    ],
  },
  conseiller: {
    submitLabel: "Envoyer ma demande",
    submitTestId: "form-contact-submit",
    fields: [
      { name: "name", label: "Nom complet", required: true },
      { name: "phone", label: "Téléphone", type: "tel", required: true },
      { name: "email", label: "Courriel", type: "email", required: true },
      {
        name: "Sujet", label: "Sujet", type: "select",
        options: ["Parler à un conseiller", "Vente de mon véhicule", "Remplacement de véhicule", "Gestion de flotte", "Conciergerie privée", "Autre"],
      },
      { name: "Message", label: "Message", type: "textarea", full: true },
    ],
  },
};

export default function LeadForm({ type, presetDetails = {}, initialValues = {}, testid }) {
  const config = FORM_CONFIGS[type];
  const [values, setValues] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const dirtyRef = useRef(new Set());

  const set = (name, v) => {
    dirtyRef.current.add(name);
    setValues((prev) => ({ ...prev, [name]: v }));
  };

  useEffect(() => {
    setValues((prev) => {
      const next = { ...prev };
      Object.entries(initialValues).forEach(([k, v]) => {
        if (!dirtyRef.current.has(k)) next[k] = v;
      });
      return next;
    });
  }, [initialValues]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const details = { ...presetDetails };
    config.fields.forEach((f) => {
      if (!["name", "phone", "email"].includes(f.name) && values[f.name]) details[f.name] = values[f.name];
    });
    try {
      await submitLead({
        type,
        name: values.name || "",
        phone: values.phone || "",
        email: values.email || "",
        details,
      });
      setSent(true);
      toast.success("Demande envoyée. Nous vous contacterons sous peu.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div data-testid={`${testid}-success`} className="flex flex-col items-center text-center py-14 px-6">
        <CheckCircle2 size={44} className="text-gold mb-5" strokeWidth={1.5} />
        <h3 className="font-display text-2xl text-white mb-3">Demande bien reçue.</h3>
        <p className="text-sm text-slate-400 font-light max-w-sm leading-relaxed">
          Un membre de notre équipe vous contactera personnellement très bientôt.
        </p>
        <button
          data-testid={`${testid}-reset`}
          onClick={() => { setSent(false); dirtyRef.current.clear(); setValues(initialValues); }}
          className="mt-7 text-[11px] uppercase tracking-[0.2em] text-gold-light hover:text-white transition-colors"
        >
          Faire une autre demande
        </button>
      </div>
    );
  }

  return (
    <form data-testid={testid} onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {config.fields.map((f) => (
        <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
          <label className="field-label" htmlFor={`${testid}-${f.name}`}>
            {f.label} {f.required && <span className="text-gold">*</span>}
          </label>
          {f.type === "select" ? (
            <select
              id={`${testid}-${f.name}`}
              data-testid={`${testid}-field-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="field appearance-none"
              value={values[f.name] || ""}
              onChange={(e) => set(f.name, e.target.value)}
              required={f.required}
            >
              <option value="" disabled>Sélectionner…</option>
              {f.options.map((o) => (
                <option key={o} value={o} className="bg-anthracite">{o}</option>
              ))}
            </select>
          ) : f.type === "textarea" ? (
            <textarea
              id={`${testid}-${f.name}`}
              data-testid={`${testid}-field-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="field min-h-[110px] resize-y"
              placeholder={f.placeholder}
              value={values[f.name] || ""}
              onChange={(e) => set(f.name, e.target.value)}
              required={f.required}
            />
          ) : (
            <input
              id={`${testid}-${f.name}`}
              data-testid={`${testid}-field-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="field"
              type={f.type || "text"}
              placeholder={f.placeholder}
              value={values[f.name] || ""}
              onChange={(e) => set(f.name, e.target.value)}
              required={f.required}
            />
          )}
        </div>
      ))}
      <div className="sm:col-span-2 mt-2">
        <button
          type="submit"
          data-testid={config.submitTestId}
          disabled={loading}
          className="btn-gold w-full sm:w-auto disabled:opacity-60 disabled:hover:translate-y-0"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          {config.submitLabel}
        </button>
      </div>
    </form>
  );
}
